import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  Home,
  Heart,
  QrCode,
  Store,
  User,
  Bell,
  ArrowLeft,
  Search,
  ChevronRight,
  Stethoscope,
  Activity,
  HeartHandshake,
  Newspaper,
  CreditCard,
  Clock,
  Receipt,
  FileText,
  HelpCircle,
  LogOut,
  Share2,
  Shield,
  ShieldCheck,
  MapPin,
  Phone,
  Globe,
  Calendar,
  DollarSign,
  Copy,
  Check,
  SlidersHorizontal,
  MessageCircle,
  Sparkles,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Partner, AssociatePlan, Associate, Purchase, Category, CardQrToken } from '../types';
import { partnerService } from '../services/partnerService';
import { planService } from '../services/planService';
import { associateService } from '../services/associateService';
import { purchaseService } from '../services/purchaseService';
import { categoryService } from '../services/categoryService';
import { cardService } from '../services/cardService';
import Loading from '../components/common/Loading';
import Alert from '../components/common/Alert';
import Modal from '../components/common/Modal';
import Badge from '../components/common/Badge';
import {
  formatPhone,
  formatCurrency,
  formatDate,
  formatCPF,
  getImageUrl,
} from '../utils/formatters';

type TabType = 'inicio' | 'beneficios' | 'cartao' | 'parceiros' | 'conta';
type BenefitCategory = 'Todos' | 'Exames' | 'Consultas' | 'Parceiros' | 'Outros';

const AssociatePartnersView: React.FC = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  // Dependentes enxergam os dados do titular: todo acesso a /associates/{id}
  // deve usar o ID do associado (user.associate_id), não o ID do próprio dependente.
  const associateId = user?.user_type === 'dependent' ? user.associate_id : user?.id;

  // Navigation tab state
  const [activeTab, setActiveTab] = useState<TabType>('inicio');

  // Core data states
  const [partners, setPartners] = useState<Partner[]>([]);
  const [filteredPartners, setFilteredPartners] = useState<Partner[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [myPlans, setMyPlans] = useState<AssociatePlan[]>([]);
  const [associateData, setAssociateData] = useState<Associate | null>(null);
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Search & Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [benefitSearch, setBenefitSearch] = useState('');
  const [benefitFilter, setBenefitFilter] = useState<BenefitCategory>('Todos');
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);

  // Vai para a aba "Parceiros", opcionalmente já filtrando por categoria.
  // Sem argumento, limpa qualquer filtro de categoria anterior.
  const goToPartners = (categoryId: number | null = null) => {
    setSelectedCategoryId(categoryId);
    setActiveTab('parceiros');
  };

  const getCategoryIcon = (nome: string) => {
    const lower = nome.toLowerCase();
    if (lower.includes('exame')) return Stethoscope;
    if (lower.includes('consulta')) return Activity;
    return Store;
  };

  // Modal states
  const [showQrModal, setShowQrModal] = useState(false);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [showPurchasesModal, setShowPurchasesModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{ url: string; alt: string } | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);

  // Live real-time clock
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Load partners from API
  const loadPartners = async () => {
    try {
      const data = await partnerService.getActive();
      setPartners(data);
      setFilteredPartners(data);
    } catch (err) {
      console.error('Erro ao carregar parceiros:', err);
      setError('Erro ao carregar parceiros. Tente recarregar a página.');
    }
  };

  // Load active categories (cards de filtro dos parceiros)
  const loadCategories = async () => {
    try {
      const data = await categoryService.getAll(true);
      setCategories(data);
    } catch (err) {
      console.error('Erro ao carregar categorias:', err);
    }
  };

  // Load associate plans from API
  const loadMyPlans = async () => {
    if (!associateId) return;
    try {
      const data = await planService.getAssociatePlans(associateId);
      setMyPlans(data);
    } catch (err) {
      console.error('Erro ao carregar planos do associado:', err);
    }
  };

  // Load full associate details
  const loadAssociateDetails = async () => {
    if (!associateId) return;
    try {
      const data = await associateService.getById(associateId);
      if (data) {
        setAssociateData(data);
        if (data.purchases && data.purchases.length > 0) {
          setPurchases(data.purchases);
        }
      }
    } catch (err) {
      console.warn('Não foi possível carregar dados estendidos do associado:', err);
    }
  };

  // Load purchases / usage history
  const loadPurchases = async () => {
    if (!associateId) return;
    try {
      const response = await purchaseService.getByAssociate(associateId);
      if (response && response.data) {
        setPurchases(response.data);
      }
    } catch (err) {
      console.warn('Não foi possível carregar histórico de compras:', err);
    }
  };

  useEffect(() => {
    const initData = async () => {
      setLoading(true);
      await Promise.all([
        loadPartners(),
        loadCategories(),
        loadMyPlans(),
        loadAssociateDetails(),
        loadPurchases(),
      ]);
      setLoading(false);
    };

    initData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [associateId]);

  // Filter partners in the "Parceiros" tab
  const filterPartners = useCallback(() => {
    const term = searchTerm.trim().toLowerCase();

    const filtered = partners.filter((partner) => {
      const matchesCategory =
        !selectedCategoryId || partner.categories?.some((c) => c.id === selectedCategoryId);

      const matchesSearch =
        !term ||
        partner.name?.toLowerCase().includes(term) ||
        partner.nome_fantasia?.toLowerCase().includes(term) ||
        partner.category?.toLowerCase().includes(term) ||
        partner.cidade?.toLowerCase().includes(term);

      return matchesCategory && matchesSearch;
    });

    setFilteredPartners(filtered);
  }, [searchTerm, partners, selectedCategoryId]);

  useEffect(() => {
    filterPartners();
  }, [filterPartners]);

  // Helpers
  const getDiscountPercentage = (partner: Partner): number => {
    return partner.discount_percentage || partner.desconto_percentual || 0;
  };

  const getActivePlansCount = (): number => {
    return myPlans.filter((plan) => plan.pivot?.status === 'ativo').length;
  };

  const getInactivePlansCount = (): number => {
    return myPlans.filter((plan) => plan.pivot?.status !== 'ativo').length;
  };

  const firstName = useMemo(() => {
    if (!user?.name) return 'Associado';
    return user.name.trim().split(' ')[0];
  }, [user?.name]);

  const memberSinceFormatted = useMemo(() => {
    const rawDate =
      associateData?.data_adesao ||
      associateData?.created_at ||
      myPlans[0]?.pivot?.data_inicio;
    if (!rawDate) return '05/2024';
    try {
      const d = new Date(rawDate);
      if (isNaN(d.getTime())) return '05/2024';
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${month}/${year}`;
    } catch {
      return '05/2024';
    }
  }, [associateData, myPlans]);

  const averageDiscount = useMemo(() => {
    if (partners.length === 0) return 0;
    const sum = partners.reduce((acc, p) => acc + getDiscountPercentage(p), 0);
    return Math.round(sum / partners.length);
  }, [partners]);

  const featuredPartners = useMemo(() => {
    return partners.filter((p) => p.destaque);
  }, [partners]);

  // QR Code do cartão virtual: URL pública de validação com o token do dia
  const [qrToken, setQrToken] = useState<CardQrToken | null>(null);

  const loadQrToken = useCallback(async () => {
    try {
      setQrToken(await cardService.getQrToken());
    } catch (err) {
      console.error('Erro ao gerar QR Code do cartão:', err);
    }
  }, []);

  useEffect(() => {
    loadQrToken();
  }, [loadQrToken]);

  // Renova o token quando o do dia expira (virada de data no servidor)
  useEffect(() => {
    if (!qrToken) return;
    const msUntilExpiry = new Date(qrToken.valid_until).getTime() - Date.now() + 1000;
    const timer = setTimeout(loadQrToken, Math.max(msUntilExpiry, 1000));
    return () => clearTimeout(timer);
  }, [qrToken, loadQrToken]);

  const qrCodeData = qrToken ? cardService.buildValidationUrl(qrToken) : '';

  // Badge de status do cartão, conforme a validação do backend
  const cardStatus = useMemo(() => {
    const status = qrToken?.status;
    if (!status) {
      return { label: 'Verificando...', badge: 'secondary' as const, pill: 'bg-gray-100 border-gray-200 text-gray-600', dot: 'bg-gray-400', indicator: '#9ca3af' };
    }
    if (status.valid) {
      return { label: 'Ativo', badge: 'success' as const, pill: 'bg-green-50 border-green-200 text-green-700', dot: 'bg-green-500', indicator: '#4ade80' };
    }
    if (status.reason === 'sem_plano') {
      return { label: 'Sem plano ativo', badge: 'warning' as const, pill: 'bg-amber-50 border-amber-200 text-amber-700', dot: 'bg-amber-500', indicator: '#fbbf24' };
    }
    return { label: 'Inativo', badge: 'danger' as const, pill: 'bg-red-50 border-red-200 text-red-700', dot: 'bg-red-500', indicator: '#f87171' };
  }, [qrToken]);

  const renderQrCode = (size: number, level: 'M' | 'H') =>
    qrCodeData ? (
      <QRCodeSVG value={qrCodeData} size={size} level={level} />
    ) : (
      <div
        className="flex items-center justify-center text-center text-[10px] text-gray-400"
        style={{ width: size, height: size }}
      >
        Gerando QR Code...
      </div>
    );

  // Share handler
  const handleShare = async () => {
    const shareUrl = window.location.origin;
    const shareData = {
      title: 'Cartão Amigos do Hospital',
      text: 'Faça parte do Amigos do Hospital e tenha acesso a descontos exclusivos em consultas, exames e comércios parceiros!',
      url: shareUrl,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (e) {
        // Fallback to modal
      }
    }
    setShowShareModal(true);
  };

  const handleCopyLink = async () => {
    const link = window.location.origin;
    let copied = false;

    // navigator.clipboard só existe em contextos seguros (HTTPS ou localhost)
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(link);
        copied = true;
      } catch (e) {
        // Fallback abaixo
      }
    }

    if (!copied) {
      const textarea = document.createElement('textarea');
      textarea.value = link;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        copied = document.execCommand('copy');
      } catch (e) {
        copied = false;
      }
      document.body.removeChild(textarea);
    }

    if (copied) {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2500);
    }
  };

  // Benefits catalog
  const benefitsCatalog = useMemo(() => {
    const categoryTiles = categories.map((category) => {
      const partnersInCategory = partners.filter((p) =>
        p.categories?.some((c) => c.id === category.id)
      );
      return {
        id: `cat-${category.id}`,
        title: `Descontos em ${category.nome}`,
        desc:
          partnersInCategory.length > 0
            ? `${partnersInCategory.length} estabelecimento(s) conveniado(s) com até ${Math.max(
                ...partnersInCategory.map((p) => getDiscountPercentage(p)),
                0
              )}% de desconto.`
            : 'Nenhum parceiro cadastrado nesta categoria ainda.',
        category: category.nome as BenefitCategory,
        icon: getCategoryIcon(category.nome),
        action: () => goToPartners(category.id),
      };
    });

    const list = [
      ...categoryTiles,
      {
        id: 4,
        title: 'Movimento Amigos do Hospital',
        desc: 'Participe ativamente das iniciativas de apoio ao hospital.',
        category: 'Outros' as BenefitCategory,
        icon: HeartHandshake,
        action: () => setShowNotificationModal(true),
      },
      {
        id: 5,
        title: 'Apoio e Solidariedade',
        desc: 'Quem doa, cuida. Quem cuida, transforma vidas juntos.',
        category: 'Outros' as BenefitCategory,
        icon: Heart,
        action: () => setShowShareModal(true),
      },
      {
        id: 6,
        title: 'Plano Amigos do Hospital',
        desc:
          myPlans.length > 0
            ? `${myPlans[0]?.nome || 'Plano Contratado'} ativo para você.`
            : 'Plano de benefícios de saúde familiar.',
        category: 'Outros' as BenefitCategory,
        icon: CreditCard,
        action: () => setShowPlansModal(true),
      },
    ];

    return list.filter((b) => {
      const matchesCategory = benefitFilter === 'Todos' || b.category === benefitFilter;
      const matchesSearch =
        !benefitSearch.trim() ||
        b.title.toLowerCase().includes(benefitSearch.toLowerCase()) ||
        b.desc.toLowerCase().includes(benefitSearch.toLowerCase());
      return matchesCategory && matchesSearch;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partners, categories, myPlans, benefitFilter, benefitSearch]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F6F8]">
      {/* =================================================================== */}
      {/* 1. VERSÃO DESKTOP (VISÍVEL APENAS EM TELAS >= 768px)                */}
      {/* =================================================================== */}
      <div className="hidden md:block">
        {/* Barra de Navegação Superior Desktop */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-xs">
          <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
            {/* Brand / Logo */}
            <div
              className="flex items-center gap-3 cursor-pointer select-none"
              onClick={() => setActiveTab('inicio')}
            >
              <div className="w-11 h-11 rounded-xl bg-[#0D8B55] text-white flex items-center justify-center shadow-md">
                <ShieldCheck size={26} />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900 leading-tight tracking-tight">
                  Amigos do Hospital
                </h1>
                <p className="text-xs text-gray-500 font-medium">Cartão de Benefícios</p>
              </div>
            </div>

            {/* Abas de Navegação Centrais */}
            <nav className="flex items-center gap-1.5 bg-gray-100 p-1.5 rounded-full border border-gray-200">
              <button
                type="button"
                onClick={() => setActiveTab('inicio')}
                className={`flex items-center gap-2 px-5 py-2 rounded-full text-xs font-bold transition-all ${
                  activeTab === 'inicio'
                    ? 'bg-[#0D8B55] text-white shadow-sm'
                    : 'text-gray-600 hover:text-[#0D8B55] hover:bg-gray-200'
                }`}
              >
                <Home size={16} />
                <span>Início</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('beneficios')}
                className={`flex items-center gap-2 px-5 py-2 rounded-full text-xs font-bold transition-all ${
                  activeTab === 'beneficios'
                    ? 'bg-[#0D8B55] text-white shadow-sm'
                    : 'text-gray-600 hover:text-[#0D8B55] hover:bg-gray-200'
                }`}
              >
                <Heart size={16} />
                <span>Benefícios</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('cartao')}
                className={`flex items-center gap-2 px-5 py-2 rounded-full text-xs font-bold transition-all ${
                  activeTab === 'cartao'
                    ? 'bg-[#0D8B55] text-white shadow-sm'
                    : 'text-gray-600 hover:text-[#0D8B55] hover:bg-gray-200'
                }`}
              >
                <QrCode size={16} />
                <span>Meu Cartão</span>
              </button>

              <button
                type="button"
                onClick={() => goToPartners()}
                className={`flex items-center gap-2 px-5 py-2 rounded-full text-xs font-bold transition-all ${
                  activeTab === 'parceiros'
                    ? 'bg-[#0D8B55] text-white shadow-sm'
                    : 'text-gray-600 hover:text-[#0D8B55] hover:bg-gray-200'
                }`}
              >
                <Store size={16} />
                <span>Parceiros</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('conta')}
                className={`flex items-center gap-2 px-5 py-2 rounded-full text-xs font-bold transition-all ${
                  activeTab === 'conta'
                    ? 'bg-[#0D8B55] text-white shadow-sm'
                    : 'text-gray-600 hover:text-[#0D8B55] hover:bg-gray-200'
                }`}
              >
                <User size={16} />
                <span>Minha Conta</span>
              </button>
            </nav>

            {/* Usuário e Ações à Direita */}
            <div className="flex items-center gap-4">
              {/* Relógio em Tempo Real */}
              <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-mono font-bold text-gray-700">
                <Clock size={14} className="text-[#0D8B55]" />
                <span>{currentTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
              </div>

              {/* Status Badge */}
              <div
                className={`flex items-center gap-1.5 px-3 py-1 border rounded-full text-xs font-bold ${cardStatus.pill}`}
                title={qrToken?.status.message}
              >
                <span className={`w-2 h-2 rounded-full animate-pulse ${cardStatus.dot}`}></span>
                <span>{cardStatus.label}</span>
              </div>

              {/* User Dropdown / Info */}
              <div
                className="flex items-center gap-2.5 cursor-pointer pl-2 border-l border-gray-200"
                onClick={() => setActiveTab('conta')}
                title="Ver perfil"
              >
                <div className="w-9 h-9 rounded-full bg-[#E8F5E9] text-[#0D8B55] font-bold flex items-center justify-center text-sm border border-green-200 shadow-xs">
                  {user?.name ? user.name.charAt(0).toUpperCase() : 'A'}
                </div>
                <div className="text-left leading-none">
                  <div className="text-xs font-bold text-gray-800">{firstName}</div>
                  <div className="text-[11px] text-gray-400 mt-0.5">
                    {user?.user_type === 'dependent' ? 'Dependente' : 'Associado'}
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={signOut}
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Sair da conta"
              >
                <LogOut size={18} />
              </button>
            </div>
          </div>
        </header>

        {/* Conteúdo Principal Desktop com Espaçamento Amplo */}
        <main className="max-w-6xl mx-auto px-6 py-8">
          {error && (
            <Alert variant="error" onClose={() => setError('')} className="mb-6">
              {error}
            </Alert>
          )}

          {/* --- TELA 1 DESKTOP: INÍCIO --- */}
          {activeTab === 'inicio' && (
            <div className="space-y-8">
              {/* 1. Hero Banner Principal */}
              <div className="bg-gradient-to-r from-[#0D8B55] to-[#075F3A] rounded-2xl p-8 text-white shadow-md relative overflow-hidden flex flex-col lg:flex-row items-center justify-between gap-6">
                <div className="space-y-3 z-10 max-w-2xl">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/15 rounded-full text-xs font-semibold backdrop-blur-sm">
                    <Sparkles size={14} className="text-green-300" />
                    <span>Benefícios Exclusivos Amigos do Hospital</span>
                  </div>
                  <h2 className="text-3xl font-extrabold tracking-tight">
                    Olá, {user?.name}! 👋
                  </h2>
                  <p className="text-sm opacity-90 leading-relaxed max-w-xl text-green-50">
                    Seja muito bem-vindo(a) ao seu portal do associado. Apresente seu cartão digital
                    para obter descontos exclusivos em exames, consultas e na rede de parceiros
                    credenciados.
                  </p>

                  <div className="flex flex-wrap items-center gap-4 pt-2 text-xs font-medium">
                    <div className="flex items-center gap-1.5 bg-white/20 px-3 py-1.5 rounded-lg">
                      <Store size={14} />
                      <span><strong>{partners.length}</strong> Parceiros Disponíveis</span>
                    </div>
                    <div className="flex items-center gap-1.5 bg-white/20 px-3 py-1.5 rounded-lg">
                      <CreditCard size={14} />
                      <span><strong>{getActivePlansCount()}</strong> Plano(s) Ativo(s)</span>
                    </div>
                    <div className="flex items-center gap-1.5 bg-white/20 px-3 py-1.5 rounded-lg">
                      <span>Desconto Médio: <strong>{averageDiscount}% OFF</strong></span>
                    </div>
                  </div>
                </div>

                {/* Card Virtual Preview no Banner */}
                <div
                  className="bg-white/10 hover:bg-white/15 border border-white/25 rounded-2xl p-5 backdrop-blur-md cursor-pointer transition-all flex items-center gap-5 shadow-lg flex-shrink-0"
                  onClick={() => setActiveTab('cartao')}
                  title="Clique para ver o cartão completo"
                >
                  <div className="space-y-1 text-left">
                    <span className="text-[10px] uppercase font-bold tracking-wider text-green-200">
                      Cartão Digital
                    </span>
                    <div className="font-bold text-base text-white">Amigos do Hospital</div>
                    <div className="text-xs text-white/80">Titular: <strong>{firstName}</strong></div>
                    <div className="text-xs text-green-300 font-semibold flex items-center gap-1 pt-1">
                      <span>Ver QR Code</span>
                      <ChevronRight size={14} />
                    </div>
                  </div>
                  <div className="bg-white p-2 rounded-xl text-gray-900 shadow-md">
                    {renderQrCode(80, 'M')}
                  </div>
                </div>
              </div>

              {/* 2. Frase de Apoio Institucional */}
              <div
                className="bg-white rounded-2xl p-5 border border-gray-200 shadow-xs hover:shadow-sm transition-shadow flex items-center justify-between cursor-pointer"
                onClick={() => setActiveTab('beneficios')}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#E8F5E9] text-[#0D8B55] flex items-center justify-center flex-shrink-0">
                    <Heart size={22} fill="currentColor" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">
                      "Quem doa, cuida. Quem cuida, transforma."
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Sua participação fortalece o Hospital e garante saúde acessível para todos.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs font-bold text-[#0D8B55]">
                  <span>Conheça as ações</span>
                  <ChevronRight size={16} />
                </div>
              </div>

              {/* 3. Seção Meus Benefícios (Grid 3 Colunas Espaçoso) */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Meus Benefícios</h3>
                    <p className="text-xs text-gray-500">Serviços e vantagens disponíveis no seu plano</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setActiveTab('beneficios')}
                    className="text-xs font-bold text-[#0D8B55] hover:underline flex items-center gap-1"
                  >
                    <span>Ver todos os benefícios</span>
                    <ChevronRight size={14} />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {categories.map((category) => {
                    const CategoryIcon = getCategoryIcon(category.nome);
                    const partnersInCategory = partners.filter((p) =>
                      p.categories?.some((c) => c.id === category.id)
                    );
                    return (
                      <div
                        key={category.id}
                        className="bg-white rounded-2xl p-6 border border-gray-200 shadow-xs hover:shadow-md hover:border-green-400 transition-all cursor-pointer flex flex-col justify-between"
                        onClick={() => goToPartners(category.id)}
                      >
                        <div>
                          <div className="w-12 h-12 rounded-xl bg-[#E8F5E9] text-[#0D8B55] flex items-center justify-center mb-4">
                            <CategoryIcon size={24} />
                          </div>
                          <h4 className="text-base font-bold text-gray-900">Descontos em {category.nome}</h4>
                          <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                            {partnersInCategory.length > 0
                              ? `${partnersInCategory.length} estabelecimento(s) conveniado(s) nesta categoria.`
                              : 'Nenhum parceiro cadastrado nesta categoria ainda.'}
                          </p>
                        </div>
                        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs">
                          <span className="text-green-600 font-semibold">
                            • {partnersInCategory.length} parceiro(s)
                          </span>
                          <span className="text-gray-400 font-medium">Ver lista →</span>
                        </div>
                      </div>
                    );
                  })}

                  <div
                    className="bg-white rounded-2xl p-6 border border-gray-200 shadow-xs hover:shadow-md hover:border-green-400 transition-all cursor-pointer flex flex-col justify-between"
                    onClick={() => setShowPlansModal(true)}
                  >
                    <div>
                      <div className="w-12 h-12 rounded-xl bg-[#E8F5E9] text-[#0D8B55] flex items-center justify-center mb-4">
                        <CreditCard size={24} />
                      </div>
                      <h4 className="text-base font-bold text-gray-900">Meus Planos</h4>
                      <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                        Consulte as coberturas, vigência e condições dos planos contratados para você e seus dependentes.
                      </p>
                    </div>
                    <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs">
                      <span className="text-green-600 font-semibold">• {getActivePlansCount()} ativo(s)</span>
                      <span className="text-gray-400 font-medium">Detalhes →</span>
                    </div>
                  </div>

                  <div
                    className="bg-white rounded-2xl p-6 border border-gray-200 shadow-xs hover:shadow-md hover:border-green-400 transition-all cursor-pointer flex flex-col justify-between"
                    onClick={() => {
                      setBenefitFilter('Outros');
                      setActiveTab('beneficios');
                    }}
                  >
                    <div>
                      <div className="w-12 h-12 rounded-xl bg-[#E8F5E9] text-[#0D8B55] flex items-center justify-center mb-4">
                        <HeartHandshake size={24} />
                      </div>
                      <h4 className="text-base font-bold text-gray-900">Movimento que Transforma</h4>
                      <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                        Acompanhe ações sociais, campanhas humanizadas e projetos desenvolvidos no Hospital.
                      </p>
                    </div>
                    <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs">
                      <span className="text-green-600 font-semibold">• Solidariedade</span>
                      <span className="text-gray-400 font-medium">Saber mais →</span>
                    </div>
                  </div>

                  <div
                    className="bg-white rounded-2xl p-6 border border-gray-200 shadow-xs hover:shadow-md hover:border-green-400 transition-all cursor-pointer flex flex-col justify-between"
                    onClick={() => setShowNotificationModal(true)}
                  >
                    <div>
                      <div className="w-12 h-12 rounded-xl bg-[#E8F5E9] text-[#0D8B55] flex items-center justify-center mb-4">
                        <Newspaper size={24} />
                      </div>
                      <h4 className="text-base font-bold text-gray-900">Notícias e Comunicados</h4>
                      <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                        Fique por dentro das últimas melhorias no hospital, novos convênios e horários de atendimento.
                      </p>
                    </div>
                    <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs">
                      <span className="text-gray-500 font-semibold">Atualizado</span>
                      <span className="text-gray-400 font-medium">Ver avisos →</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 4. Parceiros em Destaque (3 Colunas Amplas) */}
              {featuredPartners.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Parceiros em Destaque</h3>
                    <p className="text-xs text-gray-500">Descontos mais procurados pelos associados</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => goToPartners()}
                    className="text-xs font-bold text-[#0D8B55] hover:underline flex items-center gap-1"
                  >
                    <span>Ver todos ({partners.length})</span>
                    <ChevronRight size={14} />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {featuredPartners.map((partner) => (
                    <div
                      key={partner.id}
                      className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-xs hover:shadow-md transition-shadow cursor-pointer flex flex-col"
                      onClick={() => {
                        setSearchTerm(partner.name || partner.nome_fantasia || '');
                        goToPartners();
                      }}
                    >
                      <div className="relative h-44 bg-gray-100 overflow-hidden flex items-center justify-center">
                        <div className="absolute top-3 right-3 z-10 bg-[#0D8B55] text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                          {getDiscountPercentage(partner)}% OFF
                        </div>
                        {partner.logo_url ? (
                          <img
                            src={getImageUrl(partner.logo_url) || ''}
                            alt={partner.name || partner.nome_fantasia}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              (e.target as HTMLElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <Store size={48} className="text-[#0D8B55]" />
                        )}
                      </div>

                      <div className="p-5 flex-1 flex flex-col justify-between">
                        <div>
                          <div className="text-[11px] font-bold text-[#0D8B55] uppercase tracking-wider mb-1">
                            {partner.categories?.map((c) => c.nome).join(' • ') || 'Conveniados'}
                          </div>
                          <h4 className="text-base font-bold text-gray-900 line-clamp-1">
                            {partner.name || partner.nome_fantasia}
                          </h4>
                          {partner.endereco && (
                            <p className="text-xs text-gray-500 mt-1.5 flex items-center gap-1 line-clamp-1">
                              <MapPin size={13} className="text-gray-400 flex-shrink-0" />
                              <span>{partner.endereco}{partner.cidade && `, ${partner.cidade}`}</span>
                            </p>
                          )}
                        </div>

                        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-[#0D8B55] font-semibold">
                          <span>Aproveitar desconto</span>
                          <ChevronRight size={14} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              )}

              {/* 5. Banner "Indique e Transforme Vidas" Desktop */}
              <div className="bg-gradient-to-r from-[#0E8751] to-[#15A363] rounded-2xl p-8 text-white shadow-md flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="space-y-2 max-w-xl">
                  <h3 className="text-xl font-bold">Indique e transforme vidas</h3>
                  <p className="text-sm opacity-90 leading-relaxed text-green-50">
                    Convide amigos e familiares para fazer parte do Amigos do Hospital. Quanto mais
                    pessoas participam, mais forte se torna nossa rede de apoio à saúde.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleShare}
                  className="px-6 py-3 bg-white text-[#0D8B55] hover:bg-green-50 font-bold text-sm rounded-full shadow-md transition-colors flex-shrink-0"
                >
                  Indicar agora
                </button>
              </div>
            </div>
          )}

          {/* --- TELA 2 DESKTOP: BENEFÍCIOS --- */}
          {activeTab === 'beneficios' && (
            <div className="space-y-6">
              {/* Header da Página */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Benefícios Amigos do Hospital</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Explore todas as modalidades de descontos e serviços disponíveis através do seu plano.
                </p>
              </div>

              {/* Barra de Busca e Filtros Espaçosa */}
              <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-xs space-y-4">
                <div className="relative max-w-2xl">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Buscar benefícios pelo nome ou descrição..."
                    value={benefitSearch}
                    onChange={(e) => setBenefitSearch(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0D8B55] focus:bg-white transition-all"
                  />
                </div>

                {/* Filtros em Pills */}
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  {(['Todos', 'Exames', 'Consultas', 'Parceiros', 'Outros'] as const).map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setBenefitFilter(tab)}
                      className={`px-5 py-2 rounded-full text-xs font-bold transition-all ${
                        benefitFilter === tab
                          ? 'bg-[#0D8B55] text-white shadow-xs'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              </div>

              {/* Grid de Benefícios (3 Colunas) */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {benefitsCatalog.map((benefit) => {
                  const IconComp = benefit.icon;
                  return (
                    <div
                      key={benefit.id}
                      onClick={benefit.action}
                      className="bg-white rounded-2xl p-6 border border-gray-200 shadow-xs hover:shadow-md hover:border-green-400 transition-all cursor-pointer flex flex-col justify-between"
                    >
                      <div>
                        <div className="w-12 h-12 rounded-xl bg-[#0D8B55] text-white flex items-center justify-center mb-4 shadow-sm">
                          <IconComp size={22} />
                        </div>
                        <h3 className="text-base font-bold text-gray-900">{benefit.title}</h3>
                        <p className="text-xs text-gray-500 mt-2 leading-relaxed">{benefit.desc}</p>
                      </div>

                      <div className="mt-6 pt-3 border-t border-gray-100 flex items-center justify-between text-xs">
                        <span className="text-[#0D8B55] font-semibold bg-green-50 px-2.5 py-1 rounded-full">
                          • Ativo
                        </span>
                        <span className="text-gray-400 font-bold flex items-center gap-1">
                          <span>Acessar</span>
                          <ChevronRight size={14} />
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* --- TELA 3 DESKTOP: MEU CARTÃO --- */}
          {activeTab === 'cartao' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Meu Cartão de Benefícios</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Sua identidade digital oficial do Amigos do Hospital para apresentação nos parceiros.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                {/* Cartão Digital Amplo (7 Colunas) */}
                <div className="lg:col-span-7 space-y-4">
                  <div className="bg-gradient-to-br from-[#0D8B55] to-[#075F3A] rounded-2xl p-8 text-white shadow-xl relative overflow-hidden">
                    {/* Top Identity */}
                    <div className="flex items-center justify-between pb-6 border-b border-white/20">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                          <ShieldCheck size={24} className="text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-white leading-tight">
                            Amigos do Hospital
                          </h3>
                          <p className="text-xs text-green-100 opacity-90">Cartão de Benefícios</p>
                        </div>
                      </div>
                      <div className="px-3 py-1 rounded-full bg-green-400/30 border border-green-400/40 text-xs font-bold text-green-100">
                        • Ativo
                      </div>
                    </div>

                    {/* Titular & QR Code */}
                    <div className="pt-6 flex flex-col sm:flex-row sm:items-end justify-between gap-6">
                      <div className="space-y-4 text-xs">
                        <div>
                          <span className="text-green-200 text-[11px] block uppercase font-medium">
                            Nome do Titular
                          </span>
                          <strong className="text-lg font-bold text-white block mt-0.5">
                            {user?.name}
                          </strong>
                        </div>

                        <div>
                          <span className="text-green-200 text-[11px] block uppercase font-medium">
                            CPF
                          </span>
                          <strong className="font-mono text-sm tracking-wider text-white block mt-0.5">
                            {formatCPF(associateData?.cpf || user?.cpf || '')}
                          </strong>
                        </div>

                        <div>
                          <span className="text-green-200 text-[11px] block uppercase font-medium">
                            Associado Desde
                          </span>
                          <strong className="text-sm font-semibold text-white block mt-0.5">
                            {memberSinceFormatted}
                          </strong>
                        </div>
                      </div>

                      {/* QR Code Box */}
                      <div
                        className="bg-white p-3.5 rounded-2xl text-center shadow-lg cursor-pointer hover:scale-105 transition-transform flex-shrink-0 self-center sm:self-auto"
                        onClick={() => setShowQrModal(true)}
                        title="Clique para ampliar o QR Code"
                      >
                        {renderQrCode(110, 'H')}
                        <span className="text-[10px] font-bold text-gray-600 block mt-2">
                          Toque para ampliar
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-center gap-3">
                    <button
                      type="button"
                      onClick={() => setShowQrModal(true)}
                      className="px-6 py-2.5 bg-[#0D8B55] hover:bg-[#075F3A] text-white text-xs font-bold rounded-full shadow-sm transition-colors flex items-center gap-2"
                    >
                      <QrCode size={16} />
                      <span>Ampliar QR Code</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleShare}
                      className="px-6 py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs font-bold rounded-full shadow-xs transition-colors flex items-center gap-2"
                    >
                      <Share2 size={16} />
                      <span>Compartilhar</span>
                    </button>
                  </div>
                </div>

                {/* Painel Lateral: Instruções e Validação (5 Colunas) */}
                <div className="lg:col-span-5 space-y-5">
                  {/* Validação ao Vivo */}
                  <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-xs space-y-3">
                    <div className="flex items-center justify-between text-xs font-bold text-gray-700 uppercase">
                      <span className="flex items-center gap-2">
                        <Clock size={16} className="text-[#0D8B55]" />
                        Validação em Tempo Real
                      </span>
                      <span className="text-green-600">• Ao Vivo</span>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
                      <div className="text-xs text-gray-500">
                        {currentTime.toLocaleDateString('pt-BR', {
                          weekday: 'long',
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </div>
                      <div className="text-2xl font-mono font-bold text-green-700 tracking-wider mt-1">
                        {currentTime.toLocaleTimeString('pt-BR', {
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Instruções de Uso */}
                  <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-xs space-y-4">
                    <h4 className="text-sm font-bold text-gray-900">Como usar seu cartão</h4>
                    <div className="space-y-3 text-xs text-gray-600 leading-relaxed">
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-green-100 text-green-800 font-bold flex items-center justify-center flex-shrink-0 text-xs">
                          1
                        </div>
                        <p>Apresente o QR Code no caixa ou recepção do estabelecimento conveniado.</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-green-100 text-green-800 font-bold flex items-center justify-center flex-shrink-0 text-xs">
                          2
                        </div>
                        <p>O atendente fará a leitura para verificar a validade do seu benefício.</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-green-100 text-green-800 font-bold flex items-center justify-center flex-shrink-0 text-xs">
                          3
                        </div>
                        <p>Pronto! O desconto da tabela conveniada é aplicado imediatamente na sua conta.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* --- TELA 4 DESKTOP: PARCEIROS --- */}
          {activeTab === 'parceiros' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Rede de Parceiros Credenciados</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Encontre os estabelecimentos que oferecem descontos exclusivos para associados Amigos do Hospital.
                </p>
              </div>

              {/* Barra de Busca e Métricas */}
              <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-xs space-y-6">
                <div className="relative max-w-2xl">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Buscar parceiros por nome fantasia, categoria ou cidade..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0D8B55] focus:bg-white transition-all"
                  />
                </div>

                {selectedCategoryId && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-gray-500">Filtrando por:</span>
                    <button
                      type="button"
                      onClick={() => setSelectedCategoryId(null)}
                      className="inline-flex items-center gap-1.5 bg-[#0D8B55] text-white text-xs font-bold px-3 py-1.5 rounded-full hover:bg-[#0a6f44] transition-colors"
                    >
                      {categories.find((c) => c.id === selectedCategoryId)?.nome || 'Categoria'}
                      <span className="text-white/80">✕</span>
                    </button>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 border-t border-gray-100">
                  <div className="bg-gray-50 p-4 rounded-xl text-center">
                    <span className="text-xs text-gray-500">Parceiros Disponíveis</span>
                    <div className="text-2xl font-bold text-[#0D8B55] mt-1">{filteredPartners.length}</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-xl text-center">
                    <span className="text-xs text-gray-500">Desconto Médio</span>
                    <div className="text-2xl font-bold text-[#075F3A] mt-1">{averageDiscount}% OFF</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-xl text-center">
                    <span className="text-xs text-gray-500">Categorias Cadastradas</span>
                    <div className="text-2xl font-bold text-gray-800 mt-1">{categories.length}</div>
                  </div>
                </div>
              </div>

              {/* Grid de Parceiros Espaçoso (3 Colunas) */}
              {filteredPartners.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
                  <Store className="w-14 h-14 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-base font-bold text-gray-900">Nenhum parceiro encontrado</h3>
                  <p className="text-xs text-gray-500 mt-1">Tente ajustar seus termos de busca.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredPartners.map((partner) => (
                    <div
                      key={partner.id}
                      className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-xs hover:shadow-md transition-all flex flex-col justify-between"
                    >
                      {/* Logo / Imagem Header */}
                      <div className="relative h-48 bg-gray-100 flex items-center justify-center overflow-hidden">
                        <div className="absolute top-3 right-3 z-10 bg-[#0D8B55] text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                          {getDiscountPercentage(partner)}% OFF
                        </div>

                        {partner.logo_url ? (
                          <img
                            src={getImageUrl(partner.logo_url) || ''}
                            alt={partner.name || partner.nome_fantasia}
                            className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                            onClick={() =>
                              setSelectedImage({
                                url: getImageUrl(partner.logo_url) || '',
                                alt: partner.name || partner.nome_fantasia || 'Parceiro',
                              })
                            }
                            onError={(e) => {
                              (e.target as HTMLElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <Store size={48} className="text-[#0D8B55]" />
                        )}
                      </div>

                      {/* Dados do Parceiro */}
                      <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                        <div>
                          <span className="text-[11px] font-bold text-[#0D8B55] uppercase tracking-wider block mb-1">
                            {partner.categories?.map((c) => c.nome).join(' • ') || 'Conveniados'}
                          </span>
                          <h4 className="text-base font-bold text-gray-900 line-clamp-1">
                            {partner.name || partner.nome_fantasia}
                          </h4>

                          <div className="space-y-2 mt-3 text-xs text-gray-600">
                            {(partner.address || partner.endereco) && (
                              <div className="flex items-start gap-2">
                                <MapPin size={14} className="text-[#0D8B55] flex-shrink-0 mt-0.5" />
                                <span className="line-clamp-2">
                                  {partner.address || partner.endereco}
                                  {partner.cidade && `, ${partner.cidade}`}
                                  {partner.estado && ` - ${partner.estado}`}
                                </span>
                              </div>
                            )}

                            {(partner.phone || partner.telefone) && (
                              <div className="flex items-center gap-2">
                                <Phone size={14} className="text-[#0D8B55] flex-shrink-0" />
                                <a
                                  href={`tel:${partner.phone || partner.telefone}`}
                                  className="hover:underline text-gray-700 font-medium"
                                >
                                  {formatPhone(partner.phone || partner.telefone || '')}
                                </a>
                              </div>
                            )}

                            {partner.website && (
                              <div className="flex items-center gap-2">
                                <Globe size={14} className="text-[#0D8B55] flex-shrink-0" />
                                <a
                                  href={partner.website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#0D8B55] hover:underline truncate"
                                >
                                  Visitar site oficial
                                </a>
                              </div>
                            )}
                          </div>

                          {partner.observacoes && (
                            <div className="mt-3 p-2.5 bg-gray-50 rounded-lg text-[11px] text-gray-500">
                              {partner.observacoes}
                            </div>
                          )}
                        </div>

                        {/* WhatsApp CTA */}
                        {partner.whatsapp && (
                          <a
                            href={`https://wa.me/55${partner.whatsapp.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full py-2.5 px-4 bg-green-50 hover:bg-green-100 text-green-800 font-bold text-xs rounded-xl flex items-center justify-center gap-2 transition-colors"
                          >
                            <MessageCircle size={15} className="text-green-600" />
                            <span>Conversar no WhatsApp</span>
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* --- TELA 5 DESKTOP: MINHA CONTA --- */}
          {activeTab === 'conta' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Minha Conta & Configurações</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Gerencie seus dados de cadastro, consulte planos e acesse históricos do sistema.
                </p>
              </div>

              {/* Banner do Perfil Desktop */}
              <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex items-center gap-5">
                  <div className="w-16 h-16 rounded-2xl bg-[#E8F5E9] text-[#0D8B55] font-bold text-2xl flex items-center justify-center border border-green-200">
                    {user?.name ? user.name.charAt(0).toUpperCase() : 'A'}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{user?.name}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{user?.email}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs">
                      <span className="font-mono text-gray-600 font-semibold">
                        CPF: {formatCPF(associateData?.cpf || user?.cpf || '')}
                      </span>
                      <span className="text-gray-300">•</span>
                      <span className="text-green-700 font-bold">Associado Desde: {memberSinceFormatted}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setShowProfileModal(true)}
                    className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-xs rounded-xl transition-colors"
                  >
                    Ver Dados Cadastrais
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('cartao')}
                    className="px-5 py-2.5 bg-[#0D8B55] hover:bg-[#075F3A] text-white font-bold text-xs rounded-xl shadow-xs transition-colors"
                  >
                    Meu Cartão
                  </button>
                </div>
              </div>

              {/* Grid 2 Colunas: Acessos e Operações */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                {/* Coluna 1: Alternância de Perfil & Apoio */}
                <div className="space-y-6">
                  <div className="space-y-3">
                    <h3 className="text-base font-bold text-gray-900">Escolha como deseja acessar</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="bg-green-50/70 border-2 border-[#0D8B55] rounded-2xl p-5 shadow-xs flex flex-col justify-between min-h-[140px]">
                        <div>
                          <div className="w-8 h-8 rounded-lg bg-[#0D8B55] text-white flex items-center justify-center mb-3">
                            <User size={18} />
                          </div>
                          <h4 className="text-sm font-bold text-gray-900">Sou Associado</h4>
                          <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                            Acesse benefícios, descontos e seu cartão virtual do Amigos do Hospital.
                          </p>
                        </div>
                        <span className="text-xs font-bold text-[#0D8B55] mt-4">✓ Perfil Ativo</span>
                      </div>

                      <div
                        onClick={() => navigate('/admin/login')}
                        className="bg-white border border-gray-200 hover:border-green-400 rounded-2xl p-5 shadow-xs transition-all cursor-pointer flex flex-col justify-between min-h-[140px]"
                      >
                        <div>
                          <div className="w-8 h-8 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center mb-3">
                            <Shield size={18} />
                          </div>
                          <h4 className="text-sm font-bold text-gray-900">Sou Administrador</h4>
                          <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                            Gerencie associados, estabelecimentos conveniados e configurações.
                          </p>
                        </div>
                        <span className="text-xs font-bold text-gray-400 hover:text-[#0D8B55] mt-4">
                          Acessar painel →
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-xs space-y-3">
                    <h4 className="text-base font-bold text-gray-900">Amigos do Hospital</h4>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      Sua contribuição mensal salva vidas e fortalece o atendimento médico em nossa
                      região. Agradecemos sua confiança e parceria!
                    </p>
                  </div>
                </div>

                {/* Coluna 2: Operações da Conta */}
                <div className="space-y-3">
                  <h3 className="text-base font-bold text-gray-900">Operações da Conta</h3>

                  <div className="bg-white rounded-2xl border border-gray-200 divide-y divide-gray-100 shadow-xs overflow-hidden">
                    <div
                      className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => setShowPurchasesModal(true)}
                    >
                      <div className="flex items-center gap-3">
                        <Clock size={18} className="text-[#0D8B55]" />
                        <div>
                          <h4 className="text-xs font-bold text-gray-800">Histórico de Usos</h4>
                          <p className="text-[11px] text-gray-400">Consulte quando e onde você usou seus descontos</p>
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-gray-400" />
                    </div>

                    <div
                      className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => setShowPlansModal(true)}
                    >
                      <div className="flex items-center gap-3">
                        <CreditCard size={18} className="text-[#0D8B55]" />
                        <div>
                          <h4 className="text-xs font-bold text-gray-800">Meus Planos</h4>
                          <p className="text-[11px] text-gray-400">{myPlans.length} plano(s) contratado(s)</p>
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-gray-400" />
                    </div>

                    <div
                      className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => navigate('/meu-historico-financeiro')}
                    >
                      <div className="flex items-center gap-3">
                        <Receipt size={18} className="text-[#0D8B55]" />
                        <div>
                          <h4 className="text-xs font-bold text-gray-800">Histórico Financeiro</h4>
                          <p className="text-[11px] text-gray-400">Consulte faturas e comprovantes de pagamento</p>
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-gray-400" />
                    </div>

                    <div
                      className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={handleShare}
                    >
                      <div className="flex items-center gap-3">
                        <Share2 size={18} className="text-[#0D8B55]" />
                        <div>
                          <h4 className="text-xs font-bold text-gray-800">Compartilhar Cartão</h4>
                          <p className="text-[11px] text-gray-400">Envie o link do Amigos do Hospital para amigos</p>
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-gray-400" />
                    </div>

                    <div
                      className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => setShowProfileModal(true)}
                    >
                      <div className="flex items-center gap-3">
                        <FileText size={18} className="text-[#0D8B55]" />
                        <div>
                          <h4 className="text-xs font-bold text-gray-800">Meus Dados Cadastrais</h4>
                          <p className="text-[11px] text-gray-400">Mantenha seu endereço e contatos em dia</p>
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-gray-400" />
                    </div>

                    <div
                      className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => setShowNotificationModal(true)}
                    >
                      <div className="flex items-center gap-3">
                        <HelpCircle size={18} className="text-[#0D8B55]" />
                        <div>
                          <h4 className="text-xs font-bold text-gray-800">Precisa de Ajuda?</h4>
                          <p className="text-[11px] text-gray-400">Fale com nossa equipe de suporte do Hospital</p>
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-gray-400" />
                    </div>

                    <div
                      className="p-4 flex items-center justify-between hover:bg-red-50 transition-colors cursor-pointer"
                      onClick={signOut}
                    >
                      <div className="flex items-center gap-3">
                        <LogOut size={18} className="text-red-600" />
                        <div>
                          <h4 className="text-xs font-bold text-red-600">Sair da Conta</h4>
                          <p className="text-[11px] text-gray-400">Desconectar com segurança</p>
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-red-400" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* =================================================================== */}
      {/* 2. VERSÃO MOBILE (VISÍVEL APENAS EM TELAS < 768px)                  */}
      {/* =================================================================== */}
      <div className="md:hidden">
        <div className="associate-app-container">
          <div className="screen-wrapper">
            {error && (
              <Alert variant="error" onClose={() => setError('')} className="m-4">
                {error}
              </Alert>
            )}

            {/* --- TELA 1 MOBILE: INÍCIO --- */}
            {activeTab === 'inicio' && (
              <div>
                {/* 1. Header do Usuário (Hero Verde) */}
                <header className="header-hero">
                  <div className="profile-lockup">
                    {associateId ? (
                      <img
                        src={`${process.env.REACT_APP_API_URL || ''}/associates/${associateId}/photo`}
                        alt={user?.name || 'Foto'}
                        className="profile-avatar"
                        onError={(e) => {
                          (e.target as HTMLElement).style.display = 'none';
                          const fallback = (e.target as HTMLElement).nextElementSibling as HTMLElement;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      className="profile-avatar-placeholder"
                      style={{ display: associateId ? 'none' : 'flex' }}
                    >
                      {user?.name ? user.name.charAt(0).toUpperCase() : 'A'}
                    </div>
                    <div className="profile-greeting">
                      <h2>Olá, {firstName}! 👋</h2>
                      <p>Seja bem-vindo(a) ao Amigos do Hospital</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowNotificationModal(true)}
                    aria-label="Notificações"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    <Bell className="icon-bell" />
                  </button>
                </header>

                {/* 2. Cartão Virtual Compacto */}
                <div
                  className="home-virtual-card"
                  onClick={() => setActiveTab('cartao')}
                  title="Toque para abrir seu cartão"
                >
                  <div>
                    <span
                      style={{
                        fontSize: '0.68rem',
                        opacity: 0.85,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      Seu cartão
                    </span>
                    <h3 style={{ fontSize: '1.05rem', fontWeight: 700, marginTop: '2px' }}>
                      Amigos do Hospital
                    </h3>
                    <div
                      className="status-indicator"
                      style={{ '--status-color': cardStatus.indicator } as React.CSSProperties}
                    >
                      {cardStatus.label}
                    </div>
                  </div>
                  <div
                    style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      padding: '8px',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <QrCode size={34} color="#ffffff" />
                  </div>
                </div>

                {/* 3. Card Frase de Impacto */}
                <div
                  className="quote-card"
                  onClick={() => setActiveTab('beneficios')}
                  title="Conheça os benefícios"
                >
                  <div className="quote-content">
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: 'var(--primary-light)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--primary)',
                        flexShrink: 0,
                      }}
                    >
                      <Heart size={16} fill="currentColor" />
                    </div>
                    <span style={{ lineHeight: 1.3, fontWeight: 500 }}>
                      Quem doa, cuida. Quem cuida, transforma.
                    </span>
                  </div>
                  <ChevronRight size={18} color="var(--text-muted)" />
                </div>

                {/* 4. Seção Meus Benefícios */}
                <div className="section-header">
                  <h3>Meus Benefícios</h3>
                  <span className="see-all-link" onClick={() => setActiveTab('beneficios')}>
                    Ver todos
                  </span>
                </div>

                <div className="benefits-home-grid">
                  {categories.map((category) => {
                    const CategoryIcon = getCategoryIcon(category.nome);
                    return (
                      <div
                        key={category.id}
                        className="benefit-tile"
                        onClick={() => goToPartners(category.id)}
                      >
                        <div className="tile-icon-box">
                          <CategoryIcon size={20} />
                        </div>
                        <div className="tile-title">{category.nome}</div>
                        <div className="tile-desc">Descontos em {category.nome.toLowerCase()}</div>
                      </div>
                    );
                  })}

                  <div
                    className="benefit-tile"
                    onClick={() => {
                      setBenefitFilter('Outros');
                      setActiveTab('beneficios');
                    }}
                  >
                    <div className="tile-icon-box">
                      <HeartHandshake size={20} />
                    </div>
                    <div className="tile-title">Movimento</div>
                    <div className="tile-desc">Ações sociais</div>
                  </div>

                  <div
                    className="benefit-tile"
                    onClick={() => setShowNotificationModal(true)}
                  >
                    <div className="tile-icon-box">
                      <Newspaper size={20} />
                    </div>
                    <div className="tile-title">Notícias</div>
                    <div className="tile-desc">Fique por dentro</div>
                  </div>

                  <div className="benefit-tile" onClick={() => setShowPlansModal(true)}>
                    <div className="tile-icon-box">
                      <CreditCard size={20} />
                    </div>
                    <div className="tile-title">Planos</div>
                    <div className="tile-desc">
                      {getActivePlansCount()} ativo{getActivePlansCount() !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>

                {/* 5. Banner Promocional */}
                <div className="referral-banner">
                  <h3>Indique e transforme vidas</h3>
                  <p>Convide amigos e familiares para fazer parte. Juntos somos mais fortes!</p>
                  <button
                    type="button"
                    className="referral-action-btn"
                    onClick={handleShare}
                  >
                    Indicar agora
                  </button>
                </div>

                {/* 6. Seção Parceiros em Destaque */}
                {featuredPartners.length > 0 && (
                <>
                <div className="section-header">
                  <h3>Parceiros em destaque</h3>
                  <span className="see-all-link" onClick={() => goToPartners()}>
                    Ver todos
                  </span>
                </div>

                <div className="partners-grid">
                  {featuredPartners.map((partner) => (
                    <div
                      key={partner.id}
                      className="partner-card"
                      onClick={() => {
                        setSearchTerm(partner.name || partner.nome_fantasia || '');
                        goToPartners();
                      }}
                    >
                      <div className="partner-logo-container">
                        {partner.logo_url ? (
                          <img
                            src={getImageUrl(partner.logo_url) || ''}
                            alt={partner.name || partner.nome_fantasia}
                            onError={(e) => {
                              (e.target as HTMLElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <Store size={24} color="var(--primary)" />
                        )}
                      </div>
                      <div className="partner-name">
                        {partner.name || partner.nome_fantasia}
                      </div>
                      <div className="partner-discount">
                        {getDiscountPercentage(partner)}% OFF
                      </div>
                    </div>
                  ))}
                </div>
                </>
                )}
              </div>
            )}

            {/* --- TELA 2 MOBILE: BENEFÍCIOS --- */}
            {activeTab === 'beneficios' && (
              <div>
                <header className="header-bar">
                  <button
                    type="button"
                    className="back-btn"
                    onClick={() => setActiveTab('inicio')}
                    title="Voltar ao início"
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <h2 className="header-title">Benefícios</h2>
                  <div style={{ width: 32 }} />
                </header>

                <div className="search-container">
                  <div className="search-input-wrapper">
                    <Search size={18} color="var(--text-muted)" />
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Buscar benefícios..."
                      value={benefitSearch}
                      onChange={(e) => setBenefitSearch(e.target.value)}
                    />
                    <SlidersHorizontal size={18} color="var(--text-muted)" />
                  </div>
                </div>

                <div className="filter-tabs">
                  {(['Todos', 'Exames', 'Consultas', 'Parceiros', 'Outros'] as const).map(
                    (tab) => (
                      <button
                        key={tab}
                        type="button"
                        className={`tab-pill ${benefitFilter === tab ? 'active' : ''}`}
                        onClick={() => setBenefitFilter(tab)}
                      >
                        {tab}
                      </button>
                    )
                  )}
                </div>

                <div className="benefits-list">
                  {benefitsCatalog.map((benefit) => {
                    const IconComp = benefit.icon;
                    return (
                      <div
                        key={benefit.id}
                        className="benefit-row-card"
                        onClick={benefit.action}
                      >
                        <div className="benefit-row-left">
                          <div className="benefit-row-icon">
                            <IconComp size={22} />
                          </div>
                          <div className="benefit-row-info">
                            <h4>{benefit.title}</h4>
                            <p>{benefit.desc}</p>
                            <span className="status-text">• Ativo</span>
                          </div>
                        </div>
                        <ChevronRight size={18} color="var(--text-muted)" />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* --- TELA 3 MOBILE: MEU CARTÃO --- */}
            {activeTab === 'cartao' && (
              <div>
                <header className="header-bar">
                  <button
                    type="button"
                    className="back-btn"
                    onClick={() => setActiveTab('inicio')}
                    title="Voltar ao início"
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <h2 className="header-title">Meu Cartão</h2>
                  <div style={{ width: 32 }} />
                </header>

                <div className="full-card-display">
                  <div className="card-top-identity">
                    <div
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: '10px',
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ShieldCheck size={24} color="#ffffff" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#ffffff' }}>
                        Amigos do Hospital
                      </h3>
                      <p style={{ fontSize: '0.72rem', opacity: 0.85, color: '#ffffff' }}>
                        Cartão de Benefícios
                      </p>
                    </div>
                    <div
                      className="status-indicator"
                      style={
                        { marginTop: 0, color: '#ffffff', '--status-color': cardStatus.indicator } as React.CSSProperties
                      }
                    >
                      {cardStatus.label}
                    </div>
                  </div>

                  <div className="card-center-body">
                    <div className="card-user-meta">
                      <div className="meta-label">Nome</div>
                      <div className="meta-value">{user?.name}</div>

                      <div className="meta-label">CPF</div>
                      <div className="meta-value">
                        {formatCPF(associateData?.cpf || user?.cpf || '')}
                      </div>

                      <div className="meta-label">Associado desde</div>
                      <div className="meta-value" style={{ marginBottom: 0 }}>
                        {memberSinceFormatted}
                      </div>
                    </div>

                    <div
                      className="qr-code-box"
                      onClick={() => setShowQrModal(true)}
                      title="Toque para ampliar o QR Code"
                    >
                      {renderQrCode(90, 'M')}
                      <span>Toque para ampliar</span>
                    </div>
                  </div>
                </div>

                <div
                  className="instructions-card"
                  onClick={() => setShowQrModal(true)}
                >
                  <div>
                    <h4>Como usar seu cartão</h4>
                    <p>Apresente o QR Code no estabelecimento parceiro para obter seu desconto.</p>
                  </div>
                  <ChevronRight size={18} color="var(--text-muted)" />
                </div>

                <div
                  style={{
                    margin: '0 16px 14px',
                    backgroundColor: 'var(--surface-card)',
                    border: '1px solid var(--border-light)',
                    borderRadius: 'var(--radius-md)',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: 'var(--shadow-subtle)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Clock size={20} color="var(--primary)" />
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                        Comprovante em tempo real
                      </div>
                      <div
                        style={{
                          fontSize: '0.82rem',
                          fontWeight: 600,
                          color: 'var(--text-title)',
                        }}
                      >
                        {currentTime.toLocaleDateString('pt-BR', {
                          weekday: 'short',
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      fontFamily: 'monospace',
                      fontWeight: 700,
                      fontSize: '1.15rem',
                      color: 'var(--primary)',
                    }}
                  >
                    {currentTime.toLocaleTimeString('pt-BR', {
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* --- TELA 4 MOBILE: PARCEIROS --- */}
            {activeTab === 'parceiros' && (
              <div>
                <header className="header-bar">
                  <button
                    type="button"
                    className="back-btn"
                    onClick={() => setActiveTab('inicio')}
                    title="Voltar ao início"
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <h2 className="header-title">Parceiros</h2>
                  <div style={{ width: 32 }} />
                </header>

                <div className="search-container">
                  <div className="search-input-wrapper">
                    <Search size={18} color="var(--text-muted)" />
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Buscar parceiros por nome, categoria ou cidade..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '10px',
                    padding: '0 16px 14px',
                  }}
                >
                  <div
                    style={{
                      backgroundColor: 'var(--surface-card)',
                      border: '1px solid var(--border-light)',
                      borderRadius: 'var(--radius-md)',
                      padding: '10px 12px',
                      textAlign: 'center',
                      boxShadow: 'var(--shadow-subtle)',
                    }}
                  >
                    <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                      Parceiros Disponíveis
                    </div>
                    <div
                      style={{
                        fontSize: '1.25rem',
                        fontWeight: 700,
                        color: 'var(--primary)',
                        marginTop: '2px',
                      }}
                    >
                      {filteredPartners.length}
                    </div>
                  </div>

                  <div
                    style={{
                      backgroundColor: 'var(--surface-card)',
                      border: '1px solid var(--border-light)',
                      borderRadius: 'var(--radius-md)',
                      padding: '10px 12px',
                      textAlign: 'center',
                      boxShadow: 'var(--shadow-subtle)',
                    }}
                  >
                    <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                      Desconto Médio
                    </div>
                    <div
                      style={{
                        fontSize: '1.25rem',
                        fontWeight: 700,
                        color: 'var(--primary-dark)',
                        marginTop: '2px',
                      }}
                    >
                      {averageDiscount}%
                    </div>
                  </div>
                </div>

                {filteredPartners.length === 0 ? (
                  <div className="text-center py-12 px-4">
                    <Store className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <h3 className="text-base font-semibold text-gray-900 mb-1">
                      Nenhum parceiro encontrado
                    </h3>
                    <p className="text-xs text-gray-500">
                      {searchTerm
                        ? 'Tente ajustar os termos de busca'
                        : 'Aguarde novos parceiros serem adicionados'}
                    </p>
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      padding: '0 16px 16px',
                    }}
                  >
                    {filteredPartners.map((partner) => (
                      <div
                        key={partner.id}
                        style={{
                          backgroundColor: 'var(--surface-card)',
                          border: '1px solid var(--border-light)',
                          borderRadius: 'var(--radius-md)',
                          overflow: 'hidden',
                          boxShadow: 'var(--shadow-subtle)',
                        }}
                      >
                        <div
                          style={{
                            position: 'relative',
                            height: '140px',
                            backgroundColor: '#f3f4f6',
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              position: 'absolute',
                              top: '10px',
                              right: '10px',
                              backgroundColor: 'var(--primary)',
                              color: '#ffffff',
                              padding: '4px 10px',
                              borderRadius: '9999px',
                              fontWeight: 700,
                              fontSize: '0.78rem',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                              zIndex: 2,
                            }}
                          >
                            {getDiscountPercentage(partner)}% OFF
                          </div>

                          {partner.logo_url ? (
                            <img
                              src={getImageUrl(partner.logo_url) || ''}
                              alt={partner.name || partner.nome_fantasia}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                cursor: 'pointer',
                              }}
                              onClick={() =>
                                setSelectedImage({
                                  url: getImageUrl(partner.logo_url) || '',
                                  alt: partner.name || partner.nome_fantasia || 'Parceiro',
                                })
                              }
                              onError={(e) => {
                                (e.target as HTMLElement).style.display = 'none';
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'var(--primary-light)',
                              }}
                            >
                              <Store size={44} color="var(--primary)" />
                            </div>
                          )}
                        </div>

                        <div style={{ padding: '12px' }}>
                          <h3
                            style={{
                              fontSize: '0.92rem',
                              fontWeight: 700,
                              color: 'var(--text-title)',
                              marginBottom: '2px',
                            }}
                          >
                            {partner.name || partner.nome_fantasia}
                          </h3>

                          {partner.categories && partner.categories.length > 0 && (
                            <span
                              style={{
                                display: 'inline-block',
                                fontSize: '0.68rem',
                                fontWeight: 600,
                                color: 'var(--primary)',
                                marginBottom: '8px',
                              }}
                            >
                              {partner.categories.map((c) => c.nome).join(' • ')}
                            </span>
                          )}

                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '6px',
                              fontSize: '0.75rem',
                              color: 'var(--text-body)',
                            }}
                          >
                            {(partner.address || partner.endereco) && (
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                                <MapPin size={14} color="var(--primary)" style={{ flexShrink: 0, marginTop: 2 }} />
                                <span>
                                  {partner.address || partner.endereco}
                                  {partner.cidade && `, ${partner.cidade}`}
                                  {partner.estado && ` - ${partner.estado}`}
                                </span>
                              </div>
                            )}

                            {(partner.phone || partner.telefone) && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Phone size={14} color="var(--primary)" style={{ flexShrink: 0 }} />
                                <a
                                  href={`tel:${partner.phone || partner.telefone}`}
                                  style={{ color: 'inherit', textDecoration: 'none' }}
                                >
                                  {formatPhone(partner.phone || partner.telefone || '')}
                                </a>
                                {partner.whatsapp && (
                                  <a
                                    href={`https://wa.me/55${partner.whatsapp.replace(/\D/g, '')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      marginLeft: 'auto',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      fontSize: '0.7rem',
                                      color: 'var(--primary)',
                                      fontWeight: 600,
                                    }}
                                  >
                                    <MessageCircle size={14} />
                                    WhatsApp
                                  </a>
                                )}
                              </div>
                            )}

                            {partner.website && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Globe size={14} color="var(--primary)" style={{ flexShrink: 0 }} />
                                <a
                                  href={partner.website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: 'var(--primary)',
                                    textDecoration: 'none',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  Visitar site
                                </a>
                              </div>
                            )}
                          </div>

                          {partner.observacoes && (
                            <div
                              style={{
                                marginTop: '8px',
                                padding: '8px 10px',
                                backgroundColor: 'var(--bg-app)',
                                borderRadius: 'var(--radius-xs)',
                                fontSize: '0.68rem',
                                color: 'var(--text-muted)',
                              }}
                            >
                              {partner.observacoes}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* --- TELA 5 MOBILE: MINHA CONTA --- */}
            {activeTab === 'conta' && (
              <div>
                <header className="header-bar">
                  <button
                    type="button"
                    className="back-btn"
                    onClick={() => setActiveTab('inicio')}
                    title="Voltar ao início"
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <h2 className="header-title">Minha Conta</h2>
                  <div style={{ width: 32 }} />
                </header>

                <div
                  style={{
                    margin: '16px 16px 14px',
                    backgroundColor: 'var(--surface-card)',
                    border: '1px solid var(--border-light)',
                    borderRadius: 'var(--radius-md)',
                    padding: '14px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    boxShadow: 'var(--shadow-subtle)',
                  }}
                >
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: 'var(--primary-light)',
                      color: 'var(--primary)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: '1.2rem',
                      flexShrink: 0,
                    }}
                  >
                    {user?.name ? user.name.charAt(0).toUpperCase() : 'A'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3
                      style={{
                        fontSize: '0.92rem',
                        fontWeight: 700,
                        color: 'var(--text-title)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {user?.name}
                    </h3>
                    <p
                      style={{
                        fontSize: '0.72rem',
                        color: 'var(--text-muted)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {user?.email}
                    </p>
                    <div
                      className="status-indicator"
                      style={
                        { marginTop: '3px', fontSize: '0.68rem', '--status-color': cardStatus.indicator } as React.CSSProperties
                      }
                    >
                      {cardStatus.label}
                    </div>
                  </div>
                </div>

                <div className="section-header">
                  <h3>Escolha como deseja acessar</h3>
                </div>

                <div className="access-cards-grid">
                  <div
                    className="access-card"
                    style={{
                      borderColor: 'var(--primary)',
                      backgroundColor: 'var(--primary-badge-bg)',
                    }}
                  >
                    <div>
                      <div className="icon-chip green">
                        <User size={18} />
                      </div>
                      <h4>Sou Associado</h4>
                      <p>Acesse seus benefícios, veja parceiros disponíveis e economize no dia a dia.</p>
                    </div>
                    <div className="access-arrow">✓ Perfil Ativo</div>
                  </div>

                  <div
                    className="access-card"
                    onClick={() => navigate('/admin/login')}
                    title="Acessar painel administrativo"
                  >
                    <div>
                      <div className="icon-chip gray">
                        <Shield size={18} />
                      </div>
                      <h4>Sou Administrador</h4>
                      <p>Gerencie associados, parceiros e acompanhe todo o sistema.</p>
                    </div>
                    <div className="access-arrow" style={{ color: 'var(--text-muted)' }}>
                      Acessar →
                    </div>
                  </div>
                </div>

                <div className="actions-vertical-list">
                  <div
                    className="action-item-row"
                    onClick={() => setShowPurchasesModal(true)}
                  >
                    <div className="action-item-content">
                      <Clock className="action-item-icon" />
                      <div className="action-item-text">
                        <h4>Histórico de usos</h4>
                        <p>Veja onde e quando você utilizou seus descontos</p>
                      </div>
                    </div>
                    <ChevronRight size={18} color="var(--text-muted)" />
                  </div>

                  <div
                    className="action-item-row"
                    onClick={() => setShowPlansModal(true)}
                  >
                    <div className="action-item-content">
                      <CreditCard className="action-item-icon" />
                      <div className="action-item-text">
                        <h4>Meus Planos</h4>
                        <p>{myPlans.length} plano(s) vinculado(s) à sua conta</p>
                      </div>
                    </div>
                    <ChevronRight size={18} color="var(--text-muted)" />
                  </div>

                  <div
                    className="action-item-row"
                    onClick={() => navigate('/meu-historico-financeiro')}
                  >
                    <div className="action-item-content">
                      <Receipt className="action-item-icon" />
                      <div className="action-item-text">
                        <h4>Histórico financeiro</h4>
                        <p>Consulte faturas e comprovantes de pagamento</p>
                      </div>
                    </div>
                    <ChevronRight size={18} color="var(--text-muted)" />
                  </div>

                  <div className="action-item-row" onClick={handleShare}>
                    <div className="action-item-content">
                      <Share2 className="action-item-icon" />
                      <div className="action-item-text">
                        <h4>Compartilhar cartão</h4>
                        <p>Envie seu cartão ou convide familiares</p>
                      </div>
                    </div>
                    <ChevronRight size={18} color="var(--text-muted)" />
                  </div>

                  <div
                    className="action-item-row"
                    onClick={() => setShowProfileModal(true)}
                  >
                    <div className="action-item-content">
                      <FileText className="action-item-icon" />
                      <div className="action-item-text">
                        <h4>Meus dados cadastrais</h4>
                        <p>Mantenha suas informações sempre em dia</p>
                      </div>
                    </div>
                    <ChevronRight size={18} color="var(--text-muted)" />
                  </div>

                  <div
                    className="action-item-row"
                    onClick={() => setShowNotificationModal(true)}
                  >
                    <div className="action-item-content">
                      <HelpCircle className="action-item-icon" />
                      <div className="action-item-text">
                        <h4>Precisa de ajuda?</h4>
                        <p>Fale com a nossa equipe do hospital</p>
                      </div>
                    </div>
                    <ChevronRight size={18} color="var(--text-muted)" />
                  </div>

                  <div className="action-item-row" onClick={signOut}>
                    <div className="action-item-content">
                      <LogOut className="action-item-icon" style={{ color: '#dc2626' }} />
                      <div className="action-item-text">
                        <h4 style={{ color: '#dc2626' }}>Sair da conta</h4>
                        <p>Desconectar com segurança</p>
                      </div>
                    </div>
                    <ChevronRight size={18} color="var(--text-muted)" />
                  </div>
                </div>

                <div className="signup-callout-card">
                  <h4>Amigos do Hospital</h4>
                  <p>
                    Obrigado por fazer parte da rede Amigos do Hospital. Sua participação salva
                    vidas e fortalece nossa comunidade!
                  </p>
                  <button
                    type="button"
                    className="btn-primary-register"
                    onClick={() => setActiveTab('cartao')}
                  >
                    Ver Meu Cartão
                  </button>
                </div>
              </div>
            )}

            {/* Bottom Nav Mobile */}
            <nav className="global-bottom-bar" aria-label="Navegação inferior">
              <button
                type="button"
                className={`nav-tab-btn ${activeTab === 'inicio' ? 'active' : ''}`}
                onClick={() => setActiveTab('inicio')}
              >
                <Home size={20} />
                <span>Início</span>
              </button>

              <button
                type="button"
                className={`nav-tab-btn ${activeTab === 'beneficios' ? 'active' : ''}`}
                onClick={() => setActiveTab('beneficios')}
              >
                <Heart size={20} />
                <span>Benefícios</span>
              </button>

              <button
                type="button"
                className="qr-center-button"
                onClick={() => setActiveTab('cartao')}
                title="Meu Cartão / QR Code"
              >
                <QrCode size={24} />
              </button>

              <button
                type="button"
                className={`nav-tab-btn ${activeTab === 'parceiros' ? 'active' : ''}`}
                onClick={() => goToPartners()}
              >
                <Store size={20} />
                <span>Parceiros</span>
              </button>

              <button
                type="button"
                className={`nav-tab-btn ${activeTab === 'conta' ? 'active' : ''}`}
                onClick={() => setActiveTab('conta')}
              >
                <User size={20} />
                <span>Conta</span>
              </button>
            </nav>
          </div>
        </div>
      </div>

      {/* =================================================================== */}
      {/* 3. MODAIS COMPARTILHADOS (MOBILE & DESKTOP)                         */}
      {/* =================================================================== */}

      {/* Modal 1: QR Code Ampliado */}
      {showQrModal && (
        <Modal
          isOpen={showQrModal}
          onClose={() => setShowQrModal(false)}
          title="Cartão Virtual - QR Code"
          size="sm"
        >
          <div className="flex flex-col items-center justify-center p-6 text-center">
            <div className="bg-white p-5 rounded-2xl shadow-md border border-gray-100 mb-4">
              {renderQrCode(230, 'H')}
            </div>
            <h3 className="text-lg font-bold text-gray-900">{user?.name}</h3>
            <p className="text-sm text-gray-500 font-mono mt-1">
              CPF: {formatCPF(associateData?.cpf || user?.cpf || '')}
            </p>
            <div
              className={`mt-2.5 inline-flex items-center gap-1.5 px-3 py-1 border text-xs font-bold rounded-full ${cardStatus.pill}`}
            >
              <span className={`w-2 h-2 rounded-full ${cardStatus.dot}`}></span>
              {qrToken?.status.valid
                ? `${user?.user_type === 'dependent' ? 'Dependente' : 'Associado'} Ativo`
                : cardStatus.label}
            </div>
            {qrToken && !qrToken.status.valid && (
              <p className="text-xs text-gray-600 mt-2">{qrToken.status.message}</p>
            )}
            <p className="text-xs text-gray-500 mt-4 leading-relaxed max-w-xs">
              Apresente este QR Code no caixa ou recepção do parceiro para validar seu desconto imediato.
            </p>
          </div>
        </Modal>
      )}

      {/* Modal 2: Meus Planos */}
      {showPlansModal && (
        <Modal
          isOpen={showPlansModal}
          onClose={() => setShowPlansModal(false)}
          title="Meus Planos"
          size="md"
        >
          <div className="space-y-4">
            {myPlans.length === 0 ? (
              <div className="text-center py-8">
                <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600 text-sm">
                  Você ainda não possui planos contratados vinculados à conta.
                </p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {myPlans.map((plan) => (
                  <div
                    key={plan.id}
                    className="bg-gray-50 rounded-xl p-4 border border-gray-200"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <h4 className="text-base font-bold text-gray-900">{plan.nome}</h4>
                        <p className="text-lg font-bold text-green-600 mt-0.5">
                          {formatCurrency(plan.valor)}
                          <span className="text-xs text-gray-500 font-normal">/mês</span>
                        </p>
                      </div>
                      <Badge
                        variant={plan.pivot?.status === 'ativo' ? 'success' : 'secondary'}
                      >
                        {plan.pivot?.status === 'ativo' ? 'Ativo' : plan.pivot?.status || 'Inativo'}
                      </Badge>
                    </div>

                    {plan.descricao && (
                      <p className="text-gray-600 text-xs mb-3">{plan.descricao}</p>
                    )}

                    <div className="space-y-1 pt-3 border-t border-gray-200 text-xs text-gray-600">
                      {plan.pivot?.data_inicio && (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-3.5 h-3.5 text-gray-400" />
                          <span>
                            Início: <strong>{formatDate(plan.pivot.data_inicio)}</strong>
                          </span>
                        </div>
                      )}
                      {plan.pivot?.data_fim && (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-3.5 h-3.5 text-gray-400" />
                          <span>
                            Fim: <strong>{formatDate(plan.pivot.data_fim)}</strong>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="pt-4 border-t border-gray-200 grid grid-cols-2 gap-3 text-center">
              <div className="bg-green-50 rounded-xl p-3">
                <p className="text-xs text-gray-600 mb-1">Planos Ativos</p>
                <p className="text-xl font-bold text-green-600">{getActivePlansCount()}</p>
              </div>
              <div className="bg-gray-100 rounded-xl p-3">
                <p className="text-xs text-gray-600 mb-1">Planos Inativos</p>
                <p className="text-xl font-bold text-gray-600">{getInactivePlansCount()}</p>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal 3: Histórico de Utilizações */}
      {showPurchasesModal && (
        <Modal
          isOpen={showPurchasesModal}
          onClose={() => setShowPurchasesModal(false)}
          title="Histórico de Usos"
          size="md"
        >
          <div className="space-y-3">
            {purchases.length === 0 ? (
              <div className="text-center py-8">
                <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600 text-sm">
                  Nenhuma utilização de desconto registrada até o momento.
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Apresente seu QR Code nos estabelecimentos parceiros para economizar!
                </p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {purchases.map((purchase) => (
                  <div
                    key={purchase.id}
                    className="p-4 bg-gray-50 rounded-xl border border-gray-200 text-xs flex justify-between items-center"
                  >
                    <div>
                      <div className="font-bold text-gray-900 text-sm">
                        {purchase.parceiro?.nome_fantasia || purchase.parceiro?.name || 'Parceiro Conveniado'}
                      </div>
                      <div className="text-gray-500 mt-1 flex items-center gap-2">
                        <span>{formatDate(purchase.data_utilizacao || purchase.created_at)}</span>
                        {purchase.codigo_validacao && (
                          <span className="font-mono text-gray-400 text-[10px]">
                            • Cód: {purchase.codigo_validacao}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-green-600 text-base">
                        {purchase.desconto_aplicado}% OFF
                      </span>
                      {purchase.valor_final && (
                        <div className="text-gray-500 mt-0.5">
                          {formatCurrency(purchase.valor_final)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Modal 4: Meus Dados Cadastrais */}
      {showProfileModal && (
        <Modal
          isOpen={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          title="Meus Dados Cadastrais"
          size="sm"
        >
          <div className="space-y-3 text-xs">
            <div className="p-3 bg-gray-50 rounded-xl">
              <span className="text-gray-400 block text-[11px]">Nome Completo</span>
              <span className="font-bold text-gray-900 text-sm mt-0.5 block">
                {associateData?.nome || user?.name}
              </span>
            </div>

            <div className="p-3 bg-gray-50 rounded-xl">
              <span className="text-gray-400 block text-[11px]">CPF</span>
              <span className="font-bold text-gray-900 font-mono mt-0.5 block">
                {formatCPF(associateData?.cpf || user?.cpf || '')}
              </span>
            </div>

            <div className="p-3 bg-gray-50 rounded-xl">
              <span className="text-gray-400 block text-[11px]">E-mail</span>
              <span className="font-medium text-gray-800 mt-0.5 block">
                {associateData?.email || user?.email}
              </span>
            </div>

            {(associateData?.telefone || associateData?.celular) && (
              <div className="p-3 bg-gray-50 rounded-xl">
                <span className="text-gray-400 block text-[11px]">Telefone de Contato</span>
                <span className="font-semibold text-gray-800 mt-0.5 block">
                  {formatPhone(associateData.telefone || associateData.celular || '')}
                </span>
              </div>
            )}

            {associateData?.endereco && (
              <div className="p-3 bg-gray-50 rounded-xl">
                <span className="text-gray-400 block text-[11px]">Endereço Cadastrado</span>
                <span className="font-medium text-gray-800 mt-0.5 block leading-relaxed">
                  {associateData.endereco}
                  {associateData.numero && `, ${associateData.numero}`}
                  {associateData.bairro && ` - ${associateData.bairro}`}
                  {associateData.cidade && `, ${associateData.cidade}`}
                  {associateData.estado && `/${associateData.estado}`}
                </span>
              </div>
            )}

            <div className="p-3.5 bg-green-50 border border-green-200 rounded-xl flex justify-between items-center">
              <div>
                <span className="text-green-700 block text-[11px]">Início da Associação</span>
                <span className="font-bold text-green-900 text-sm">{memberSinceFormatted}</span>
              </div>
              <Badge variant={cardStatus.badge}>{cardStatus.label}</Badge>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal 5: Notificações e Informações do Hospital */}
      {showNotificationModal && (
        <Modal
          isOpen={showNotificationModal}
          onClose={() => setShowNotificationModal(false)}
          title="Avisos & Comunicados"
          size="sm"
        >
          <div className="space-y-3">
            <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-center gap-2 text-green-800 font-bold text-sm mb-1.5">
                <Heart size={16} /> Bem-vindo(a) ao Amigos do Hospital!
              </div>
              <p className="text-xs text-green-700 leading-relaxed">
                Apresente seu cartão digital em qualquer estabelecimento credenciado para usufruir
                dos descontos e vantagens exclusivas da sua associação.
              </p>
            </div>

            <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
              <div className="font-bold text-gray-800 text-sm mb-1">Central de Atendimento</div>
              <p className="text-xs text-gray-600 leading-relaxed">
                Dúvidas sobre coberturas de planos ou horários de atendimento? Entre em contato com
                nossa equipe de apoio no Hospital.
              </p>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal 6: Compartilhar / Indicar */}
      {showShareModal && (
        <Modal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          title="Indique e Transforme Vidas"
          size="sm"
        >
          <div className="text-center p-4">
            <div className="w-14 h-14 rounded-full bg-green-100 text-green-700 flex items-center justify-center mx-auto mb-3">
              <Share2 size={26} />
            </div>
            <h4 className="font-bold text-gray-900 text-base mb-1.5">
              Compartilhe o Amigos do Hospital
            </h4>
            <p className="text-xs text-gray-600 mb-5 leading-relaxed max-w-sm mx-auto">
              Convide amigos e familiares para apoiar a saúde e economizar em exames, consultas e
              comércios conveniados.
            </p>

            <button
              type="button"
              onClick={handleCopyLink}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-[#0D8B55] hover:bg-[#075F3A] text-white text-xs font-bold rounded-xl transition-colors shadow-sm"
            >
              {copiedLink ? <Check size={16} /> : <Copy size={16} />}
              {copiedLink ? 'Link Copiado com Sucesso!' : 'Copiar Link de Convite'}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal 7: Imagem do Parceiro Ampliada */}
      {selectedImage && (
        <Modal
          isOpen={!!selectedImage}
          onClose={() => setSelectedImage(null)}
          title={selectedImage.alt}
        >
          <div className="flex items-center justify-center p-4 overflow-auto">
            <img
              src={selectedImage.url}
              alt={selectedImage.alt}
              className="max-w-full max-h-[70vh] object-contain rounded-xl shadow-lg"
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AssociatePartnersView;
